import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatMoney } from '@johnsaks/core'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { EmptyState } from '@/components/empty-state'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import { fmtDateTime } from '@/lib/labels'

type CartRow = {
  id: string
  updatedAt: string
  remindedAt: string | null
  customer: {
    id: string
    email: string
    firstName: string
    phone: string | null
  } | null
  items: Array<{
    id: string
    quantity: number
    product: { id: string; sku: string; name: string; priceCents: number }
  }>
}

export function PaniersAbandonnes() {
  const toast = useToast()
  const qc = useQueryClient()
  const [remindId, setRemindId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['admin-abandoned-carts'],
    queryFn: () => api<{ items: CartRow[] }>('/admin/abandoned-carts?limit=100'),
  })

  const remind = useMutation({
    mutationFn: () => apiMutate(`/admin/abandoned-carts/${remindId}/remind`, 'POST'),
    onSuccess: () => {
      toast.ok('Relance enregistrée')
      setRemindId(null)
      void qc.invalidateQueries({ queryKey: ['admin-abandoned-carts'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = list.data?.items ?? []

  return (
    <div className="flex flex-col gap-5">
      {list.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState
          title="Aucun panier abandonné"
          body="Les paniers inactifs avec articles apparaîtront ici."
        />
      ) : (
        <section className="carte-admin overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Client', 'Articles', 'Valeur', 'MAJ', 'Relance', ''].map((h) => (
                  <th key={h || 'a'} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const value = c.items.reduce(
                  (s, i) => s + i.quantity * i.product.priceCents,
                  0,
                )
                return (
                  <tr key={c.id} className="border-b border-bordure-admin last:border-0">
                    <td className="px-4 py-2.5">
                      {c.customer ? (
                        <div>
                          <p className="font-medium">{c.customer.firstName}</p>
                          <p className="text-xs text-second-admin">{c.customer.email}</p>
                        </div>
                      ) : (
                        <span className="text-second-admin">Invité</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-second-admin">
                      {c.items.map((i) => `${i.product.sku} ×${i.quantity}`).join(', ')}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">{formatMoney(value)}</td>
                    <td className="px-4 py-2.5 text-second-admin">{fmtDateTime(c.updatedAt)}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill
                        label={c.remindedAt ? fmtDateTime(c.remindedAt) : 'Jamais'}
                        tone={c.remindedAt ? 'ok' : 'warn'}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <RowActions
                        items={[
                          {
                            label: 'Relancer',
                            onSelect: () => setRemindId(c.id),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}

      <ConfirmDialog
        open={Boolean(remindId)}
        title="Relancer ce panier ?"
        body="Une relance sera enregistrée (et un message pourra être envoyé selon les modèles)."
        confirmLabel="Relancer"
        busy={remind.isPending}
        onClose={() => setRemindId(null)}
        onConfirm={() => remind.mutate()}
      />
    </div>
  )
}
